import { Form, Formik } from 'formik';
import { camelCase, snakeCase } from 'lodash';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useWindowSize } from 'usehooks-ts';

import FilterSign from 'images/directory/filter_sign.svg';

import { useAppContext } from '../../contexts/AppContext';
import { useDirectoryFilterOptions, useProfile, useSubscriberDirectory } from '../../hooks';
import { edit_profile_settings_path, insights_path, subscriber_profile_path } from '../../routes';
import { AutoSave, Spinner } from '../BaseUI';
import TextField from '../FormFields/TextField';
import { EnableProfileModal } from '../shared';
import ClearFilterSection from './ClearFilterSection';
import FilterNav from './FilterNav';
import Subscribers from './Subscribers';

export const INITIAL_FORM_VALUES = {
  company: '',
  topics: [],
  openTo: [],
  skills: [],
  favoriteVacationSpots: [],
  favoriteSportPlay: [],
  favoriteSportWatch: [],
  favoriteBook: [],
  favoriteShow: [],
  location: '',
  fragment: '',
  listDisplay: 'all',
  page: 1,
};

const INITIAL_SHOW_FILTERS_STATE = {
  company: false,
  topics: false,
  openTo: true,
  skills: false,
  location: false,
};

// converts search params to two objects, one for backend and one for form:
// [{query_one: val, another: [val2a, val2b]}, {queryOne: val, another: [val2a, val2b]}]
// example decoded search params: ?open_to=hiring||finding_new_job&company=google&location=new york city
const sanitizeSearchParams = (searchParams) => {
  const sanitizedSearchParams = {};
  const formSearchParams = {};
  for (const [key, value] of searchParams) {
    const decodedValue = decodeURIComponent(value.toLowerCase());
    // if key in initial state is an array, then value should be converted to array
    sanitizedSearchParams[snakeCase(key)] = Array.isArray(INITIAL_FORM_VALUES[camelCase(key)])
      ? decodedValue.split('||')
      : decodedValue;
    formSearchParams[camelCase(key)] = Array.isArray(INITIAL_FORM_VALUES[camelCase(key)])
      ? decodedValue.split('||')
      : decodedValue;
  }
  return [sanitizedSearchParams, formSearchParams];
};

const Layout = () => {
  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
  const [showSlideFilter, setShowSlideFilter] = useState(false);
  const [showFilterMap, setShowFilterMap] = useState(INITIAL_SHOW_FILTERS_STATE);
  const [displayedFilterOptions, setDisplayedFilterOptions] = useState();
  const { width } = useWindowSize();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useProfile();
  const [sanitizedSearchParams, formSearchParams] = useMemo(
    () => sanitizeSearchParams(searchParams),
    [searchParams]
  );
  const { filterOptions, areFilterOptionsLoading } =
    useDirectoryFilterOptions(sanitizedSearchParams);
  const { subscribersData, isLoadingSubscriberDirectory } =
    useSubscriberDirectory(sanitizedSearchParams);
  const { isWebview } = useAppContext();

  useEffect(() => {
    if (!isLoadingSubscriberDirectory) {
      setFormValues({ ...INITIAL_FORM_VALUES, ...formSearchParams });

      // show filters as open on page load if passed in query params
      const onLoadShowFilterMap = Object.fromEntries(
        Object.entries(formSearchParams).map(([param, value]) => [
          param,
          !isEqual(value, INITIAL_FORM_VALUES[param]),
        ])
      );
      setShowFilterMap((prevState) => ({
        ...prevState,
        ...onLoadShowFilterMap,
      }));
    }
  }, [isLoadingSubscriberDirectory, formSearchParams]);

  useEffect(() => {
    if (!areFilterOptionsLoading) {
      setDisplayedFilterOptions(filterOptions);
    }
  }, [areFilterOptionsLoading, filterOptions]);

  useEffect(() => {
    if (width > 768) {
      setShowSlideFilter(false);
    }
  }, [width]);

  const updateSearchParams = (sanitizedParams) => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(sanitizedParams)) {
      if (!value || isEqual(value, INITIAL_FORM_VALUES[camelCase(key)])) {
        updatedSearchParams.delete(key);
      } else {
        const queryStringVal = encodeURIComponent(Array.isArray(value) ? value.join('||') : value);
        updatedSearchParams.set(key, queryStringVal);
      }
    }
    setSearchParams(updatedSearchParams, { replace: true });
  };

  const handleSubmit = async (values) => {
    const { page: _, ...prevFormValues } = formValues;
    const { page: newPage, ...newFormValues } = values;

    const sanitizedParams = {
      company: values.company,
      topics: values.topics,
      skills: values.skills,
      location: values.location,
      open_to: values.openTo,
      favorite_vacation_spots: values.favoriteVacationSpots,
      favorite_sport_play: values.favoriteSportPlay,
      favorite_sport_watch: values.favoriteSportWatch,
      favorite_book: values.favoriteBook,
      favorite_show: values.favoriteShow,
      fragment: values.fragment,
      list_display: values.listDisplay,
      // if any of the filters change besides page, reset page value to 1
      page: isEqual(prevFormValues, newFormValues) ? newPage : 1,
    };

    updateSearchParams(sanitizedParams, searchParams);
  };

  return (
    <div id="SubscriberDirectory">
      {!displayedFilterOptions ? (
        <Spinner />
      ) : (
        <Container>
          {!subscribersData?.canViewDirectory && !isLoadingSubscriberDirectory && (
            <EnableProfileModal
              isLoggedIn={true}
              isSubscriber={subscribersData.currentUserStatus > 1}
            />
          )}
          {!isWebview ? (
            <>
              <Row className="header-container">
                <Col className="col-sm-12 col-md-7">
                  <h3 className="header-text">Subscriber Directory</h3>
                  <div className="subheader-text">
                    Make connections worth your time without all the noise. Search, find and engage
                    with others who are serious about tech and business. See{' '}
                    <a href={insights_path()} className="subheader-text-link">
                      insights
                    </a>{' '}
                    about the subscriber base.
                  </div>
                </Col>
                <Col className="col-sm-12 col-md-5 buttons-header-wrapper btn-container">
                  <div className="buttons-wrapper">
                    <Button
                      className="profile-button margin"
                      onClick={() => (window.location = subscriber_profile_path(profile.username))}
                    >
                      View My Profile
                    </Button>
                    <Button
                      className="profile-button --white"
                      onClick={() => (window.location = edit_profile_settings_path())}
                    >
                      Update My Profile
                    </Button>
                  </div>
                </Col>
              </Row>
              <Row className="divider">
                <div className="col-sm-12">
                  <hr />
                </div>
              </Row>
            </>
          ) : (
            <div className="mt2" />
          )}

          <Formik initialValues={formValues} enableReinitialize={true} onSubmit={handleSubmit}>
            {(formik) => (
              <Form onSubmit={formik.handleSubmit}>
                <AutoSave debounceMs={500} />
                <Row className="filters-header-container">
                  <Col className="col-sm-12 col-md-4 filters-container">
                    <Button
                      className={`profile-button --grey --full-width ${
                        width < 992 ? '' : '--no-pointer'
                      }`}
                      onClick={() => setShowSlideFilter(!showSlideFilter)}
                    >
                      <span>
                        <img src={FilterSign} />
                        <span> Filters</span>
                      </span>
                    </Button>
                  </Col>
                  <Col className="col-sm-12 col-md-8 search-container">
                    <div className="search-section">
                      <TextField
                        placeholder="Search for subscribers by name"
                        name="fragment"
                        icon={<i className="fa fa-search search-toggler" />}
                      />
                    </div>
                  </Col>
                </Row>
                <Row className="filters-section-container">
                  <Col
                    className={`col-sm-12 col-md-4 filters-section ${
                      showSlideFilter ? 'show' : ''
                    }`}
                  >
                    <FilterNav
                      {...displayedFilterOptions}
                      showSlideFilter={showSlideFilter}
                      setShowSlideFilter={setShowSlideFilter}
                      showFilterMap={showFilterMap}
                      setShowFilterMap={setShowFilterMap}
                      setDisplayedFilterOptions={setDisplayedFilterOptions}
                      handleResetForm={() => formik.setValues(INITIAL_FORM_VALUES)}
                    />
                  </Col>
                  <Col className="col-sm-12 col-md-8">
                    <ClearFilterSection {...displayedFilterOptions} />
                    {/* hidden button prevents the first button in the first subscriber list from
                    being 'clicked' when user hits enter button. Hacky but nothing else worked */}
                    <button style={{ display: 'none' }}></button>
                    <div
                      className={`list-section ${
                        isLoadingSubscriberDirectory ? 'top-center' : ''
                      } ${showSlideFilter ? 'hide' : ''}`}
                    >
                      <Subscribers
                        profiles={subscribersData?.profiles || []}
                        isLoadingSubscriberDirectory={isLoadingSubscriberDirectory}
                        totalPages={subscribersData?.totalPages || 0}
                        currentUserId={subscribersData?.currentUserId}
                        descriptionField={
                          formik.values.openTo.length === 1 && formik.values.openTo[0]
                        }
                      />
                      <TextField name="page" type="number" hidden={true} />
                    </div>
                  </Col>
                </Row>
                {!isWebview && (
                  <Row>
                    <Col className="col-sm-12 col-md-4 mobile-nav-btn">
                      <Button
                        className="profile-button"
                        onClick={() => (window.location = edit_profile_settings_path())}
                      >
                        Update My Profile
                      </Button>
                    </Col>
                  </Row>
                )}
              </Form>
            )}
          </Formik>
        </Container>
      )}
    </div>
  );
};

export default Layout;
